import { useEffect } from "react";
import { useId, useState } from "react";
import type { ReactNode } from "react";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";

export interface DestructiveDialogProps {
	isOpen: boolean;
	title: string;
	description: string;
	onConfirm: () => void;
	onCancel: () => void;
	typeToConfirm?: {
		enabled: boolean;
		phrase: string;
	};
	warning?: string;
	/** Optional additional content rendered between description and warning */
	details?: ReactNode;
	/** Optional label for the confirm button (defaults to "Löschen") */
	confirmLabel?: string;
}

/**
 * A destructive dialog with configurable text and the option to "type-to-confirm"
 * @param props.isOpen - Whether the dialog is open.
 * @param props.title - The title of the dialog.
 * @param props.description - The description of the dialog.
 * @param props.onConfirm - The function to call when the confirm button is clicked.
 * @param props.onCancel - The function to call when the cancel button is clicked.
 * @param props.typeToConfirm - The type to confirm.
 * @param props.warning - The warning of the dialog.
 * @param props.details - The details of the dialog. Optional additional content rendered between description and warning
 * @param props.confirmLabel - The label for the confirm button.
 * @returns
 */
export function DestructiveDialog({
	isOpen,
	title,
	description,
	onConfirm,
	onCancel,
	typeToConfirm,
	warning,
	details,
	confirmLabel = "Löschen",
}: DestructiveDialogProps) {
	const [confirmText, setConfirmText] = useState("");
	const isConfirmDisabled =
		typeToConfirm?.enabled && confirmText !== typeToConfirm.phrase;

	// Reset the confirmation text when dialog opens/closes
	useEffect(() => {
		if (!isOpen) {
			setConfirmText("");
		}
	}, [isOpen]);

	const inputId = useId();

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>

				{details && (
					<div className="mt-3 mb-1 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 space-y-2">
						{details}
					</div>
				)}

				{warning && (
					<div className="flex">
						<p className="text-sm font-bold">{warning}</p>
					</div>
				)}

				{typeToConfirm?.enabled && (
					<div className="my-4">
						<label htmlFor={inputId} className="text-sm font-medium block mb-2">
							Dies kann nicht rückgängig gemacht werden. Bitte mit "
							{typeToConfirm.phrase}" bestätigen
						</label>
						<input
							id={inputId}
							type="text"
							className="w-full px-3 py-2 border rounded-md"
							value={confirmText}
							onChange={(e) => setConfirmText(e.target.value)}
							placeholder={`Bitte mit "${typeToConfirm.phrase}" bestätigen`}
						/>
					</div>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={onCancel}>
						Abbrechen
					</Button>
					<Button
						variant="destructive"
						onClick={onConfirm}
						disabled={isConfirmDisabled}
					>
						{confirmLabel}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
